import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { FaqService } from 'src/app/services/faq/faq.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-faqs',
  templateUrl: './md-faqs.component.html',
  styleUrls: ['./md-faqs.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class MdFaqsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  faqs: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedFaqs: string[] = [];

  constructor(
    private _faqService: FaqService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadFaqs();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadFaqs(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedFaqs = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._faqService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.faqs = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.faqs = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading FAQs:', error);
          this.faqs = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadFaqs(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadFaqs(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadFaqs();
  }

  openAddFaqModal(): void {
    const modalElement = document.getElementById('kt_modal_faq_add_edit');
    if (modalElement) {
      // Clear any existing data
      modalElement.setAttribute('data-faq-id', '');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(faq: any): void {
    const modalElement = document.getElementById('kt_modal_faq_add_edit');
    if (modalElement) {
      // Set the FAQ ID as a data attribute
      modalElement.setAttribute('data-faq-id', faq._id);
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteFaq(faq: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete this FAQ. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(faq);
      }
    });
  }

  private performDelete(faq: any): void {
    const faqId = faq._id || faq.id;
    this.subscriptions.push(
      this._faqService.DeleteDataById(faqId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'FAQ has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadFaqs(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete FAQ.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting FAQ.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onFaqAdded(): void {
    console.log('FAQ added, refreshing list...');
    this.loadFaqs(this.currentPage, this.searchTerm, this.filters);
  }

  onFaqUpdated(): void {
    console.log('FAQ updated, refreshing list...');
    this.loadFaqs(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(faqId: string): boolean {
    return this.selectedFaqs.includes(faqId);
  }

  isAllSelected(): boolean {
    return this.faqs.length > 0 && this.selectedFaqs.length === this.faqs.length;
  }

  isIndeterminate(): boolean {
    return this.selectedFaqs.length > 0 && this.selectedFaqs.length < this.faqs.length;
  }

  toggleSelection(faqId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedFaqs.includes(faqId)) {
        this.selectedFaqs.push(faqId);
      }
    } else {
      this.selectedFaqs = this.selectedFaqs.filter(id => id !== faqId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedFaqs = this.faqs.map(faq => faq._id);
    } else {
      this.selectedFaqs = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-faq-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-faq-table-toolbar="selected"]');

    if (this.selectedFaqs.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedFaqs(): void {
    if (this.selectedFaqs.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedFaqs.length} selected FAQ(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedFaqs.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._faqService.BulkDelete(this.selectedFaqs).subscribe({
        next: () => {
          this.loadFaqs();
          this.selectedFaqs = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected FAQs have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting FAQs:', error);
          Swal.fire('Error!', 'Failed to delete selected FAQs.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
